import { ArrowBackIcon, CloseIcon, LockIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormLabel,
    Input,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    Switch,
    useColorModeValue,
} from '@chakra-ui/react';
import { emailRepository } from 'api/EmailRepository';
import Card from 'components/card/Card';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface EmailTemplate {
    id: string;
    name: string;
    channel: string;
    notificationType: string;
    subType: string;
    subject: string;
    body: string;
    isActive: boolean;
}

type AlertStatus = 'success' | 'info' | 'warning' | 'error';

export default function EditEmailForm() {
    const { state } = useLocation();
    const email: EmailTemplate = state?.email || {};
    const navigate = useNavigate();
    const textColor = useColorModeValue('secondaryGray.900', 'white');

    const [formData, setFormData] = useState({
        id: email.id || '',
        name: email.name || '',
        channel: email.channel || '',
        notificationType: email.notificationType || '',
        subType: email.subType || '',
        subject: email.subject || '',
        body: email.body || '',
        isActive: email.isActive ?? true,
    });

    useEffect(() => {
        if (email) {
            setFormData({
                id: email.id || '',
                name: email.name || '',
                channel: email.channel || '',
                notificationType: email.notificationType || '',
                subType: email.subType || '',
                subject: email.subject || '',
                body: email.body || '',
                isActive: email.isActive ?? true,
            });
        }
    }, [email]);


    const [centerAlertVisible, setCenterAlertVisible] = useState(false);
    const [centerAlertMessage, setCenterAlertMessage] = useState('');
    const [centerAlertStatus, setCenterAlertStatus] = useState<AlertStatus>('success');
    const centerAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showCenteredAlert = (
        msg: string,
        status: AlertStatus = 'success',
        duration = 2000
    ) => {
        if (centerAlertTimerRef.current) {
            clearTimeout(centerAlertTimerRef.current);
            centerAlertTimerRef.current = null;
        }
        setCenterAlertMessage(msg);
        setCenterAlertStatus(status);
        setCenterAlertVisible(true);
        centerAlertTimerRef.current = setTimeout(() => {
            setCenterAlertVisible(false);
            centerAlertTimerRef.current = null;
        }, duration);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleUpdate = async () => {
        try {

            await emailRepository.updateEmail(formData.id, {
                name: formData.name,
                channel: formData.channel,
                notificationType: formData.notificationType,
                subType: formData.subType,
                subject: formData.subject,
                body: formData.body,
                isActive: formData.isActive,
            });

            showCenteredAlert(" Template updated successfully", "success");
            setTimeout(() => {
                navigate(`/admin/Template/TemplateList`,);
            }, 1200);
        } catch (error) {
            showCenteredAlert("Failed to update template", "error");
        }
    };


    return (
        <Flex
            direction="column"
            minH="100vh"
            align="center"
            position="relative"
            px={{ base: '20px', md: '40px' }}
            pb="60px"
        >
            <Box
                position="absolute"
                top="90"
                left="0"
                w="100%"
                h="40vh"
                bgGradient="linear(to-b, brand.400, brand.600)"
                zIndex={0}
                borderRadius="20px"
            />

            <Card
                w={{ sm: '330px', md: '700px', lg: '850px' }}
                p="30px"
                mt={{ base: '150px', md: '120px' }}
                zIndex={1}
                borderRadius="16px"
            >
                <Flex justify="space-between" align="center" mb="20px">
                    <Text color={textColor} fontSize="2xl" fontWeight="700">
                        Edit Template
                    </Text>
                    <Button
                        variant="ghost"
                        colorScheme="gray"
                        onClick={() => navigate(`/admin/Template/TemplateList`)}
                        p={0}
                    >
                        <CloseIcon w={3} h={3} />
                    </Button>
                </Flex>

                <Flex direction="column" w="100%">
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                        <Stack gap="20px">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Name
                                </FormLabel>
                                <Input
                                    name="name"
                                    placeholder="e.g. Password Reset "
                                    value={formData.name}
                                    onChange={handleChange}
                                    h="44px"
                                    color={textColor}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Channel
                                </FormLabel>
                                <Input
                                    name="channel"
                                    placeholder="e.g. Email, SMS"
                                    value={formData.channel}
                                    onChange={handleChange}
                                    h="44px"
                                    color={textColor}
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Notification
                                </FormLabel>
                                <Input
                                    name="notification"
                                    placeholder="Enter notification name"
                                    value={formData.notificationType}
                                    onChange={handleChange}
                                    h="44px"
                                    color={textColor}
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Subtype
                                </FormLabel>
                                <Input
                                    name="subtype"
                                    placeholder="Enter subtype"
                                    value={formData.subType}
                                    onChange={handleChange}
                                    h="44px"
                                    color={textColor}
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Subject
                                </FormLabel>
                                <Input
                                    name="subject"
                                    placeholder="e.g. Reset Your Password"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    h="44px"
                                    color={textColor}
                                />
                            </FormControl>
                        </Stack>

                        <Stack gap="20px">
                            <FormControl display="flex" alignItems="center" pt="32px">
                                <FormLabel mb="0" fontWeight="bold" fontSize="sm" color={textColor}>
                                    Active
                                </FormLabel>
                                <Switch
                                    name="isActive"
                                    colorScheme="blue"
                                    isChecked={formData.isActive}
                                    onChange={handleSwitchChange}
                                    size="lg"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                                    Body
                                </FormLabel>
                                <Textarea
                                    name="body"
                                    placeholder="Enter email body content..."
                                    value={formData.body}
                                    onChange={handleChange}
                                    rows={6}
                                    color={textColor}
                                />
                            </FormControl>
                        </Stack>
                    </SimpleGrid>

                    <Flex justify="center" gap="20px" mt="32px">
                        <Button variant="darkBrand" w={{ base: '128px', md: '148px' }} onClick={handleUpdate}>
                            Update
                        </Button>
                    </Flex>
                </Flex>
            </Card>




            {centerAlertVisible && (
                <Center
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    zIndex={60}
                    pointerEvents="none"
                >
                    <Alert
                        status={centerAlertStatus}
                        variant="solid"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        textAlign="center"
                        width="600px"
                        borderRadius="14px"
                        boxShadow="xl"
                        py={10}
                        px={16}
                        pointerEvents="auto"
                    >
                        <AlertIcon boxSize="40px" mr={0} />

                        <Text fontSize="lg" fontWeight="700" mt={1}>
                            {centerAlertMessage}
                        </Text>
                    </Alert>
                </Center>
            )}
        </Flex>
    );
}